// Lightweight input formatters used across consumer/clinic forms so user
// typed values mirror the canonical display format. Each formatter is a
// pure function: it accepts the raw user input and returns the formatted
// string. Pair with `useSmartInput` to wire into a controlled <input>.

/**
 * Format a US-style phone number as the user types.
 * - "5551234"             -> "(555) 123-4"
 * - "5551234567"          -> "(555) 123-4567"
 * - "+15551234567"        -> "+1 (555) 123-4567"
 * - non-digit chars are stripped before re-formatting.
 *
 * Up to 10 digits + optional leading "1" country code. Anything past 11
 * digits is dropped silently so the display never overflows.
 */
export function formatPhone(value) {
  if (value == null) return "";
  const raw = String(value);
  const digits = raw.replace(/\D/g, "").slice(0, 11);
  if (digits.length === 0) return "";

  let countryPrefix = "";
  let body = digits;
  if (digits.length === 11 && digits.startsWith("1")) {
    countryPrefix = "+1 ";
    body = digits.slice(1);
  } else if (digits.length === 11) {
    // Treat any 11-digit prefix as a country code.
    countryPrefix = `+${digits[0]} `;
    body = digits.slice(1);
  }

  if (body.length <= 3) return `${countryPrefix}(${body}`;
  if (body.length <= 6) return `${countryPrefix}(${body.slice(0, 3)}) ${body.slice(3)}`;
  return `${countryPrefix}(${body.slice(0, 3)}) ${body.slice(3, 6)}-${body.slice(6, 10)}`;
}

/**
 * Format a hex color like "ff6600" -> "#FF6600". Tolerates a leading "#"
 * and lowercases anything outside [0-9A-Fa-f]. Truncates to 6 hex chars.
 */
export function formatHexColor(value) {
  if (value == null) return "";
  const cleaned = String(value).replace(/[^0-9A-Fa-f]/g, "").slice(0, 6).toUpperCase();
  return cleaned ? `#${cleaned}` : "";
}

/**
 * Format an URL — auto-prepend "https://" when the user types a bare host.
 */
export function formatUrl(value) {
  const v = String(value || "").trim();
  if (!v) return "";
  if (/^https?:\/\//i.test(v)) return v;
  if (v.startsWith("//")) return `https:${v}`;
  return v;
}

/**
 * Format a US-style ZIP code.
 *  - "12345" -> "12345"
 *  - "123456789" -> "12345-6789"
 */
export function formatZip(value) {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 9);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
}
